import { useState, useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable } from '@/shared/components/ui/data-table'
import { Search, Tag, Hash, Wifi, Smartphone, Calendar } from 'lucide-react'
import type { Registro, CampoFormulario } from '../types'

interface RegistrosTableProps {
  registros: Registro[]
  campos: CampoFormulario[]
  actions?: React.ReactNode
}

const tipoLabel: Record<string, string> = {
  canje: 'Canje',
  solicitud: 'Solicitud',
  intento_fallido: 'Fallido',
}

const tipoVariant = (tipo: string) => {
  if (tipo === 'canje') return 'default' as const
  if (tipo === 'solicitud') return 'secondary' as const
  return 'destructive' as const
}

export function RegistrosTable({ registros, campos, actions }: RegistrosTableProps) {
  const [tipoFilter, setTipoFilter] = useState('todos')
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    let result = registros
    if (tipoFilter !== 'todos') {
      result = result.filter(r => r.tipo === tipoFilter)
    }
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(r =>
        r.ip_address?.toLowerCase().includes(q) ||
        r.codigo?.toLowerCase().includes(q) ||
        r.codigo_intentado?.toLowerCase().includes(q) ||
        Object.values(r.datos_formulario || {}).some(v => String(v).toLowerCase().includes(q))
      )
    }
    return result
  }, [registros, tipoFilter, search])

  const columns: ColumnDef<Registro>[] = useMemo(() => [
    {
      accessorKey: 'tipo',
      header: () => (
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4" />
          Tipo
        </div>
      ),
      cell: ({ row }) => {
        const tipo = row.getValue('tipo') as string
        return (
          <Badge variant={tipoVariant(tipo)}>
            {tipoLabel[tipo] || tipo}
          </Badge>
        )
      },
    },
    {
      id: 'codigo_display',
      header: () => (
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4" />
          Código
        </div>
      ),
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {row.original.codigo || row.original.codigo_intentado || '-'}
        </span>
      ),
    },
    ...campos.map((campo): ColumnDef<Registro> => ({
      id: `campo_${campo.id}`,
      header: campo.etiqueta,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.datos_formulario?.[campo.nombre_campo] || '-'}
        </span>
      ),
    })),
    {
      accessorKey: 'ip_address',
      header: () => (
        <div className="flex items-center gap-2">
          <Wifi className="h-4 w-4" />
          IP
        </div>
      ),
      cell: ({ row }) => (
        <span className="text-sm">{row.getValue('ip_address') || '-'}</span>
      ),
    },
    {
      accessorKey: 'dispositivo',
      header: () => (
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4" />
          Dispositivo
        </div>
      ),
      cell: ({ row }) => (
        <span className="text-sm">{row.getValue('dispositivo') || '-'}</span>
      ),
    },
    {
      accessorKey: 'fecha_registro',
      header: () => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Fecha
        </div>
      ),
      cell: ({ row }) => {
        const fecha = row.getValue('fecha_registro') as string | null
        return (
          <span className="text-sm">
            {fecha
              ? new Date(fecha).toLocaleDateString('es-EC', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })
              : '-'
            }
          </span>
        )
      },
    },
  ], [campos])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por email, código, IP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="canje">Canjes</SelectItem>
            <SelectItem value="solicitud">Solicitudes</SelectItem>
            <SelectItem value="intento_fallido">Fallidos</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} registros</span>
        {actions && <div className="ml-auto">{actions}</div>}
      </div>

      <DataTable columns={columns} data={filtered} defaultPageSize={10} />
    </div>
  )
}
